import { Node, mergeAttributes, VueNodeViewRenderer } from '@tiptap/vue-3'
import EditorSection from './NodeViews/EditorSection.vue'
import { v4 as uuidv4 } from 'uuid'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    section: {
      /**
       * Add a section based on a template variable
       */
      addSection: () => ReturnType
      /**
       * Remove a section
       */
      removeSection: () => ReturnType
      /**
       * Split a section at the current cursor position
       */
      splitSection: () => ReturnType
    }
  }
}

export interface SectionOptions {
  HTMLAttributes: Record<string, unknown>
}

export const ExtensionSection = Node.create<SectionOptions>({
  name: 'section',
  content: 'block+',
  group: 'section',
  defining: true,
  draggable: true,
  isolating: true,
  // allowGapCursor: true,
  addOptions() {
    return {
      HTMLAttributes: { class: 'condition' },
    }
  },
  addAttributes() {
    return {
      optional: {
        default: false,
      },
      'numbered-paragraph': {
        default: true,
      },
      'numbered-paragraph-value': {
        default: undefined,
      },
      code: {
        default: uuidv4(),
        renderHTML: attributes => {
          return {
            'data-section-code': attributes.code,
          }
        },
        parseHTML: element => element.getAttribute('data-section-code'),
      },
      label: {
        default: null,
        renderHTML: attributes => {
          return {
            'data-section-label': attributes.label,
          }
        },
        parseHTML: element => element.getAttribute('data-section-label'),
      },
    }
  },
  renderHTML({ HTMLAttributes }) {
    return ['editor-section', mergeAttributes(HTMLAttributes, this.options.HTMLAttributes), 0]
  },
  addCommands() {
    return {
      addSection:
        () =>
        ({ commands }) => {
          return commands.wrapIn(this.name)
        },
      removeSection:
        () =>
        ({ commands }) => {
          return commands.lift(this.name)
        },
      splitSection:
        () =>
        ({ state, dispatch }) => {
          const { selection } = state
          const { $from } = selection

          // Find the section node that contains the cursor
          let sectionDepth = -1
          let sectionNode = null
          let sectionPos = -1

          for (let depth = $from.depth; depth > 0; depth--) {
            const node = $from.node(depth)
            if (node.type.name === 'section') {
              sectionDepth = depth
              sectionNode = node
              sectionPos = $from.start(depth)
              break
            }
          }

          // If no section found, return false
          if (sectionDepth === -1 || !sectionNode) {
            return false
          }

          // Check if cursor is at valid split position (not at boundaries)
          const sectionStart = $from.start(sectionDepth)
          const sectionEnd = $from.end(sectionDepth)
          const cursorPos = $from.pos

          if (cursorPos <= sectionStart || cursorPos >= sectionEnd || sectionNode.content.size === 0) {
            return false
          }

          // Calculate the split position relative to the section content
          const splitPos = cursorPos - sectionStart - 1

          // Split the section content at the cursor position
          const beforeContent = sectionNode.content.cut(0, splitPos)
          const afterContent = sectionNode.content.cut(splitPos)

          // If either part is empty, don't split
          if (beforeContent.size === 0 || afterContent.size === 0) {
            return false
          }

          if (dispatch) {
            const tr = state.tr

            // Create new section with same attributes but new UUID
            const newSectionAttrs = {
              ...sectionNode.attrs,
              code: uuidv4(),
            }

            // Create the new section node with after content
            const newSection = state.schema.nodes.section.create(newSectionAttrs, afterContent)

            // Delete the content after the split position from the original section
            tr.delete(cursorPos, sectionEnd - 1)

            // Insert the new section after the original section
            const originalSectionEndPos = sectionPos + sectionNode.nodeSize - (sectionNode.content.size - beforeContent.size)
            tr.insert(originalSectionEndPos, newSection)

            // Set cursor to the beginning of the new section
            const newCursorPos = originalSectionEndPos + 1
            tr.setSelection(state.selection.constructor.near(tr.doc.resolve(newCursorPos)))

            dispatch(tr)
          }

          return true
        },
    }
  },
  addNodeView() {
    return VueNodeViewRenderer(EditorSection)
  },
})
export default ExtensionSection
